/*--------------------------------------------------------------------------
 * Define valor inicial do campo caso haja um atributo "value" no HTML
 -------------------------------------------------------------------------*/
const setDefaultValueIfNoInternalValue = function(field, nodes)
{

	let has_internal_value = field.getValue() !== undefined;
	let has_default_value = nodes[0]?.hasAttribute('value');

	if (!has_internal_value && has_default_value)
	{
		field.setValue(nodes[0].getAttribute('value'));
	}

};

/*--------------------------------------------------------------------------
 * Para input com list (datalist): resolve node.value (pode ser label) para o value da option
 -------------------------------------------------------------------------*/
const resolveDatalistValue = function(node, node_value)
{
	if (!node?.list?.options?.length || node_value == null || node_value === '')
		return node_value;
	const trimmed = String(node_value).trim();
	const option = Array.from(node.list.options).find(opt => opt.innerText && opt.innerText.trim() === trimmed);
	return option ? option.value : node_value;
};

/*--------------------------------------------------------------------------
 * Só invoca setValue() se o valor no elemento foi alterado
 -------------------------------------------------------------------------*/
const setValueIfChanged = function(field, node)
{

	let field_value = field.format(field.getValue());
	let field_has_value = Boolean(field_value);

	let node_value = node.value;
	let node_has_value = Boolean(node_value.length);
	// Em campo com datalist, o node mostra o label; manter/salvar o value (id) da option
	if (node.list)
	{
		node_value = resolveDatalistValue(node, node_value) ?? node_value;
		// Se o input está vazio mas o campo já tem valor (id), opções podem ainda não ter carregado – não sobrescrever
		if (!node_has_value && field_has_value && (typeof field.getValue() === 'number' || (typeof field.getValue() === 'string' && field.getValue().length > 0)))
			return void field.checkErrorState();
	}

	let is_update_needed = (field_has_value || node_has_value) && (node_value != field_value);

	if (is_update_needed)
	{
		field.setValue(node_value);
		field.refresh();
	}

	field.checkErrorState();	// Clear error states also when the field is emptied

};

const field = {

	/*--------------------------------------------------------------------------
	 * selector: seletor CSS do elemento na tela
	 * events: array de eventos DOM a capturar
	 * regex: expressão regular usada para validar e formatar o dado
	 * filter: expressão regular para remover caracteres antes da validação
	 * store_mask: máscara de formatação para armazenamento do dado
	 * display_mask: máscara de formatação para exibição do dado
	 -------------------------------------------------------------------------*/
	selector: null,									// CSS selector, usually an id
	events: ['keydown', 'change', 'blur'],			// DOM events to be handled
	regex: /^(.*)$/,								// Regex for validation/formatting
	filter: /[]/g,									// Chars to be filtered out
	store_mask: '$1',								// Store mask
	display_mask: '$1',								// Display mask
	
	/*--------------------------------------------------------------------------
	 * Cria um clone de field com as propriedades e tipos especificados
	 * properties são aplicadas (assigned) diretamente no clone criado e
	 * types são novos objetos encadeados através de herança prototípica
	 -------------------------------------------------------------------------*/
	create(properties, types) 
	{
		let field = Object.assign(Object.create(this), properties);
		types?.forEach(type => 
		{
			if (type)
				field = this.appendType.call(field, type);
			else
				console.error(properties, 'Undefined type');
		});
		return field;
	},

	/*--------------------------------------------------------------------------
	 * Retorna novo objeto encadeado com as propriedades do tipo especificado
	 -------------------------------------------------------------------------*/
	appendType(type)
	{
		return Object.assign(Object.create(this), type);
	},

	/*--------------------------------------------------------------------------
	 * Remove os últimos objetos encadeados escalando os prototypes
	 -------------------------------------------------------------------------*/
	scrapeTypes(count)
	{
		let proto, that = this;
		for (let i = 0; i < count; i++)
		{
			proto = Object.getPrototypeOf(that);
			that = proto;
		}
		return that;
	},

	/*--------------------------------------------------------------------------
	 * Retorna um identificador baseado no form e no id/classe do elemento
	 -------------------------------------------------------------------------*/
	getId()			// Used as key for internal storage
	{
		let node = document.querySelector(this.selector);
		if (!node)
			console.log('Selector not found:', this.selector, this);
		let node_id = node?.id || node?.className;
		let form_id = node?.form?.getAttribute?.('id') || node?.form?.className || '';		// getAttribute required
		return `${form_id}.${node_id}`;
	},

	/*--------------------------------------------------------------------------
	 * Retorna o nome do campo com base no atributo "name"
	 -------------------------------------------------------------------------*/
	getName()		// Used as key for assembling JSON objects
	{
		let node = document.querySelector(this.selector);
		return node?.getAttribute('name') || node?.id || node?.className;
	},

	/*--------------------------------------------------------------------------
	 * Retorna o texto do label associado ao elemento
	 -------------------------------------------------------------------------*/
	getLabel()		// Used as reference for displaying messages to the user
	{
		let node = document.querySelector(this.selector);
		return node?.labels[0]?.innerText.trim() || node?.className || node?.id;
	},

	/*--------------------------------------------------------------------------
	 * Retorna o valor armazenado do campo ou um valor default
	 -------------------------------------------------------------------------*/
	getValue(default_value)
	{
		let value = sessionStorage.getItem(this.getId());	// Return null if key doesn't exist
		return value ?? default_value;
	},

	/*--------------------------------------------------------------------------
	 * Armazena um novo valor
	 -------------------------------------------------------------------------*/
	setValue(value)
	{
		value = this.format(value, this.store_mask);
		sessionStorage.setItem(this.getId(), value);
	},

	/*--------------------------------------------------------------------------
	 * Apaga o valor armazenado na sessionStorage
	 -------------------------------------------------------------------------*/
	eraseValue()
	{
		sessionStorage.removeItem(this.getId());
	},

	/*--------------------------------------------------------------------------
	 * Informa se o dado contido no campo é válido ou inválido
	 * Não deve testar outros estados (por exemplo, se está vazio ou não)
	 -------------------------------------------------------------------------*/
	checkValue(value = this.getValue())
	{
		return this.regex.test(String(value ?? '').replace(this.filter, ''));
	},

	/*--------------------------------------------------------------------------
	 * Formata dados válidos conforme a máscara de exibição
	 -------------------------------------------------------------------------*/
	format(value = this.getValue(), mask = this.display_mask)
	{
		value = String(value ?? '');
		return this.checkValue(value) ? value.replace(this.filter, '').replace(this.regex, (typeof mask == 'function' ? mask.bind(this) : mask)) : value;
	},

	/*--------------------------------------------------------------------------
	 * Exibe o valor armazenado de forma formatada no elemento
	 -------------------------------------------------------------------------*/
	refresh()
	{
		let node = document.querySelector(this.selector)
		if (!node)
			return void console.error(this.selector, 'Not found');
		// input[type=file]: o valor só pode ser ''; o navegador não permite atribuir filename
		if (node.type === 'file')
			return;
		node.value = this.format();
	},

	/*--------------------------------------------------------------------------
	 * Define o estado de erro conforme se o campo:
	 * 	- Não estiver desabilitado
	 * 	- For requerido e estiver vazio
	 * 	- Estiver preenchido com um dado inválido
	 -------------------------------------------------------------------------*/
	checkErrorState()
	{
		this.setErrorState(!this.isDisabled() && (this.isRequired() || !this.isEmpty()) && !this.checkValue());
	},

	/*--------------------------------------------------------------------------
	 * Liga ou desliga o flag de erro do campo
	 -------------------------------------------------------------------------*/
	setErrorState(state)
	{
		let node = document.querySelector(this.selector);
		if (node && state)
			node.setAttribute('data-error', true);
		else if (node?.hasAttribute('data-error'))
			node.removeAttribute('data-error');
	},

	/*--------------------------------------------------------------------------
	 * Informa o estado do flag de erro do campo
	 -------------------------------------------------------------------------*/
	getErrorState()
	{
		return document.querySelector(this.selector)?.hasAttribute('data-error') || false;
	},

	/*--------------------------------------------------------------------------
	 * Define o campo como readonly (não funciona para SELECT)
	 -------------------------------------------------------------------------*/
	setReadOnly()
	{
		document.querySelectorAll(this.selector).forEach(node =>		// Selector may refer to multiple radio buttons
		{
			node.setAttribute('readonly', Number(node.getAttribute('readonly')) + 1);
		});
	},

	/*--------------------------------------------------------------------------
	 * Remove o atributo readonly do campo
	 -------------------------------------------------------------------------*/
	setReadWrite()
	{
		document.querySelectorAll(this.selector).forEach(node =>
		{
			let count = Number(node.getAttribute('readonly')) - 1;
			if (count > 0)
				node.setAttribute('readonly', count);
			else if (count == 0)
				node.removeAttribute('readonly');
		});
	},

	/*--------------------------------------------------------------------------
	 * Desabilita o campo para edição
	 -------------------------------------------------------------------------*/
	disableInput()
	{
		document.querySelectorAll(this.selector).forEach(node =>		// Selector may refer to multiple radio buttons
		{
			node.setAttribute('disabled', Number(node.getAttribute('disabled')) + 1);
		});
	},

	/*--------------------------------------------------------------------------
	 * Habilita o campo para edição
	 -------------------------------------------------------------------------*/
	enableInput()
	{
		document.querySelectorAll(this.selector).forEach(node =>
		{
			let count = Number(node.getAttribute('disabled')) - 1;
			if (count > 0)
				node.setAttribute('disabled', count);
			else if (count == 0)
				node.removeAttribute('disabled');
		});
	},

	/*--------------------------------------------------------------------------
	 * Informa se o campo está vazio
	 -------------------------------------------------------------------------*/
	isEmpty(value = this.getValue())
	{
		return value === undefined || value === null || value === '';	// MUST NOT use !value
	},

	/*--------------------------------------------------------------------------
	 * Informa se o campo é mandatório
	 -------------------------------------------------------------------------*/
	isRequired()
	{
		return document.querySelector(this.selector)?.hasAttribute('required') || false;
	},

	/*--------------------------------------------------------------------------
	 * Informa se o campo é readonly
	 -------------------------------------------------------------------------*/
	isReadOnly()
	{
		return document.querySelector(this.selector)?.hasAttribute('readonly') || false;
	},

	/*--------------------------------------------------------------------------
	 * Informa se o campo está desabilitado
	 -------------------------------------------------------------------------*/
	isDisabled()
	{
		return document.querySelector(this.selector)?.hasAttribute('disabled') || false;
	},

	/*--------------------------------------------------------------------------
	 * Inicializa o valor e inicia a monitoração do campo
	 -------------------------------------------------------------------------*/
	captureEvents()
	{
		let nodes = document.querySelectorAll(this.selector);
		setDefaultValueIfNoInternalValue(this, nodes);
		nodes.forEach(node =>
		{
			this.events.forEach(event =>
			{
				node.addEventListener(event, this.handleEvents.bind(this));
			});
		});
	},

	/*--------------------------------------------------------------------------
	 * Cria um clone do nó desassociando todos os eventos previamente capturados
	 * removeEventListener() não funciona por conta do bind() na captura
	 -------------------------------------------------------------------------*/
	releaseEvents()
	{
		document.querySelectorAll(this.selector).forEach(node =>
		{
			node.replaceWith(node.cloneNode(true));
		});
	},

	/*--------------------------------------------------------------------------
	 * Trata os eventos capturados
	 * Esta rotina padrão armazena o valor interno quando o elemento é alterado
	 -------------------------------------------------------------------------*/
	handleEvents(ev)
	{
		switch (ev.type)
		{
			case 'keydown':
				if (ev.key == 'Enter')
					setValueIfChanged(this, ev.target);
				break;
			case 'blur':
			case 'change':
				setValueIfChanged(this, ev.target);
				break;
			default:
				this.setValue(ev.target.value).refresh();
		}
	},

	/*--------------------------------------------------------------------------
	 * Invoca a função de mesmo nome no protótipo do objeto
	 *
	 * O exemplo abaixo invocará getValue() no protótipo do objeto que invocou 
	 * safeProtoCall():
	 *
	 * 		getValue(_this = this)
	 * 		{
	 * 			this.safeProtoCall(_this);
	 * 		}
	 *
	 * Isso permite que múltiplos tipos possam ser encadeados a um field:
	 *
	 * field  company_id  pubsub  datalist
	 * +---+  +--------+  +----+  +------+
	 * |   |--|        |--|    |--|      |
	 * +---+  +--------+  +----+  +------+
	 *            ^                   ^
	 *           this               _this		(exemplo)
	 *
	 * Cada tipo é um objeto encadeado no anterior com seus próprios métodos,
	 * mas que podem invocar métodos cadeia acima.
	 * 
	 * "this" não deve ser manipulado com call, apply, bind, pois senão não
	 * é mais possível identificar o protótipo do objeto em questão.
	 *
	 * "_this" deve ser passado como último argumento à função e deve apontar 
	 * para o último objeto da cadeia (o objeto completamente tipificado).
	 *
	 * Quando a chamada chega à raiz da cadeia, ou seja, a "field", 
	 * safeProtoCall() invoca o protótipo com "this" apontando para "_this", 
	 * de forma que as ações de field sejam aplicadas sobre o objeto no final 
	 * da cadeia. Para ser capaz de fazer essa transição, safeProtoCall() 
	 * precisa estar sempre no objeto logo acima de field (obviamente pode 
	 * estar adicionalmente em outras posições).
	 *
	 * Para identificar se chegamos à raiz, estamos testando se o objeto tem
	 * uma função própria chamada "safeProtoCall". Portanto, estamos assumindo
	 * que esta função não será overloaded em outros pontos da cadeia.
	 -------------------------------------------------------------------------*/
	safeProtoCall(...args)		// Last argument expected to be 'this' pointing to the last object in the chain; When used, safeProtoCall must be the first in the chain;
	{
		let proto, that = this;
		let caller = this.getCaller();
		while (true)
		{
			proto = Object.getPrototypeOf(that);
			if (proto[caller] != this[caller])
				break;
			that = proto;
		}
		let at_root = Object.hasOwn(Object.getPrototypeOf(proto), 'safeProtoCall');
		return proto[caller].call(at_root ? args.pop() : proto, ...args);
	},

	/*--------------------------------------------------------------------------
	 * Identifica a função está chamando safeProtoCall()
	 * Usa uma funcionalidade não-standard dos navegadores para obter a stack 
	 * trace. A resposta varia conforme o navegador.
	 -------------------------------------------------------------------------*/
	getCaller()
	{
		let stack = (new Error()).stack.split('\n');
		if (navigator.userAgent.includes('Chrome'))
			return stack[3].match(/\.(\w+)\b/)[1];
		else if (navigator.userAgent.includes('Firefox'))
			return stack[2].match(/^(\w+)@/)[1];
		else
			throw new Error(`Unexpected User Agent: ${navigator.userAgent}`);
	}

};

export default field;

